import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAuth } from "../AuthProvider";
import { clearAuthTokens } from "../utils/operatorAuth";
import promoImg from "./assets/promo.png";
import { Dialog } from "./fleet/ui";
import useUnread from "./useUnread";
import { mediaUrl } from "./account/media";
import sampleCompany from "../assets/images/sample-company.svg";

/**
 * Sidebar + topbar from the wireframes (HTML/iTruck_Fleet_Requests_2.html).
 * Markup/classNames are the wireframe's own; CSS comes from the page's scoped
 * stylesheet (`.cab-<page>`), so this component must render inside <CabPage>.
 */
const Icon = ({ d }) => (
  <svg aria-hidden="true" className="icon" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" viewBox="0 0 24 24">
    <path d={d} />
  </svg>
);

/** Company logo from Настройки, or the stock company icon when none is uploaded. */
const Avatar = ({ photo }) => (
  <span className={`avatar has-photo${photo ? "" : " is-sample"}`}>
    <img className={photo ? undefined : "is-sample"} src={photo || sampleCompany} alt="" />
  </span>
);

export const NAV = [
  { label: "Главная", to: "/", d: "M3 10 12 3l9 7 M5 9v12h5v-7h4v7h5V9", match: (p) => p === "/" },
  { label: "Поиск", to: "/search", d: "M16 10a6 6 0 1 1-12 0 6 6 0 0 1 12 0 M15 15l6 6", match: (p) => p.startsWith("/search") },
  { label: "Опубликовать груз", to: "/order", d: "M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0 M12 8v8 M8 12h8", match: (p) => p === "/order" || p.startsWith("/orders/create") },
  { label: "Мой транспорт", to: "/my-fleet", d: "M2 5h12v13H2z M14 10h4l4 5v3h-8 M5 18a2 2 0 1 0 .01 0 M17 18a2 2 0 1 0 .01 0", match: (p) => p.startsWith("/my-fleet") },
  { label: "Мои грузы", to: "/my-orders", d: "m12 3 9 5-9 5-9-5z M3 8v10l9 5 9-5V8 M12 13v10 M8 5l9 5", match: (p) => p.startsWith("/my-orders") },
  { label: "Сообщения", to: "/chats", d: "M5 4h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7l-5 3V7a3 3 0 0 1 3-3 M8 9h7 M8 13h3", match: (p) => p.startsWith("/chats") },
  { label: "Контрагенты", to: "/companies", d: "M15 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0 M5 21v-3a7 7 0 0 1 14 0v3 M18 4a3 3 0 0 1 0 6 M21 20v-5", match: (p) => p.startsWith("/companies") },
  { label: "Аналитика", to: "/analytics", d: "M3 21h19 M5 18v-5 M10 18V9 M15 18v-5 M20 18V5 M3 10l7-6 5 3 6-5", match: (p) => p.startsWith("/analytics") },
  { label: "Платежи", to: "/payments", d: "M3 5h18v15H3z M3 10h18 M6 15h4", match: (p) => p.startsWith("/payments") },
  { label: "Уведомления", to: "/notifications", d: "M5 17h14l-2-4V9a5 5 0 0 0-10 0v4z M10 21h4", match: (p) => p.startsWith("/notifications") },
  { label: "Настройки", to: "/settings", d: "m9 3 6 0 1 3 3 1 2 5-2 5-3 1-1 3H9l-1-3-3-1-2-5 2-5 3-1z M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0", match: (p) => p.startsWith("/settings") },
];

const ROLE_LABEL = {
  COMPANY: "Бизнес аккаунт",
  DISPATCHER: "Диспетчер",
  DRIVER: "Водитель",
  ADMIN: "Администратор",
  CLIENT: "Грузовладелец",
};

export function useCabUser() {
  const user = useSelector((s) => s.auth.user) || {};
  let profile = user.profile || null;
  try {
    const auth = useAuth(); // eslint-disable-line react-hooks/rules-of-hooks
    profile = auth?.companyProfileData || profile; // refreshed by updateProfile() after «Сохранить» in Настройки
  } catch {
    /* outside AuthProvider */
  }
  const companyName = String(profile?.company_profile?.company_name || "").trim() || null;
  const person = [user.first_name, user.last_name].filter(Boolean).join(" ");
  const name = companyName || person || user.username || "Аккаунт";
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  // the logo uploaded in Настройки → Профиль компании
  const photo = mediaUrl(profile?.company_profile?.profile_images) || "";
  return { name, initials, photo, roleLabel: ROLE_LABEL[user.role] || "Аккаунт", user };
}

export default function CabShell({ unread }) {
  const liveUnread = useUnread();
  const badge = unread ?? liveUnread;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { name, photo, roleLabel, user } = useCabUser();
  const [dialog, setDialog] = useState(null); // "account" | "language"
  const person = [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username || "";

  // wireframe: ⌘K / Ctrl+K focuses the global search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        document.getElementById("global-search")?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  const logout = () => {
    clearAuthTokens();
    window.location.href = "/login";
  };
  return (
    <>
      <a className="skip" href="#main">К содержимому</a>
      <aside className="sidebar">
        <a aria-label="iTruck — Главная" className="logo" href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }}>
          <svg aria-hidden="true" viewBox="0 0 20 31">
            <circle cx="13" cy="5" fill="#0063ff" r="3.3" />
            <path d="M7 12c2-2 6-2 8 0l-4 12c-1 4-6 4-7 1l3-8H3c-2 0-2-2 0-3z" fill="#0063ff" />
          </svg>
          <span>iTruck</span>
        </a>
        <p className="tagline">Больше грузов. Меньше пустых рейсов.</p>
        <nav aria-label="Основная навигация">
          {NAV.map((n) => {
            const active = n.match(pathname);
            return (
              <button
                key={n.to}
                className={`nav-item${active ? " active" : ""}`}
                aria-current={active ? "page" : undefined}
                onClick={() => navigate(n.to)}
              >
                <Icon d={n.d} />
                <span>{n.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="sidebar-bottom">
          <div className="promo">
            <svg aria-label="Надёжные перевозчики для вашего бизнеса" className="promo-art" preserveAspectRatio="xMidYMid meet" role="img" viewBox="22 622 229 225">
              <image href={promoImg} width="1672" height="941" />
            </svg>
            <button aria-label="Разместить груз" className="promo-link" onClick={() => navigate("/order")} />
          </div>
          <button className="account" onClick={() => setDialog("account")}>
            <Avatar photo={photo} />
            <span>
              <strong>{name}</strong>
              <small>{roleLabel}</small>
            </span>
            <Icon d="M7 6h15 M7 12h15 M7 18h15 M2 6h1 M2 12h1 M2 18h1" />
          </button>
        </div>
      </aside>
      <header className="topbar">
        <a aria-label="iTruck — Главная" className="mobile-logo" href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }}>
          <svg aria-hidden="true" viewBox="0 0 20 31">
            <circle cx="13" cy="5" fill="#0063ff" r="3.3" />
            <path d="M7 12c2-2 6-2 8 0l-4 12c-1 4-6 4-7 1l3-8H3c-2 0-2-2 0-3z" fill="#0063ff" />
          </svg>
          <span>iTruck</span>
        </a>
        <label className="global-search">
          <Icon d="M16 10a6 6 0 1 1-12 0 6 6 0 0 1 12 0 M15 15l6 6" />
          <input
            aria-label="Поиск по грузам, машинам, компаниям"
            id="global-search"
            placeholder="Поиск по грузам, машинам, компаниям..."
            onKeyDown={(e) => {
              const q = e.currentTarget.value.trim();
              if (e.key === "Enter" && q) {
                // pages that implement the wireframe's search behaviour call preventDefault()
                const ev = new CustomEvent("cab:global-search", { detail: { q }, cancelable: true });
                window.dispatchEvent(ev);
                if (!ev.defaultPrevented) navigate(`/search?mode=loads&q=${encodeURIComponent(q)}`);
              }
            }}
          />
          <kbd>⌘ K</kbd>
        </label>
        <div className="top-actions">
          <button
            aria-label="Уведомления"
            title={badge > 0 ? `Новых: ${badge}` : "Уведомления"}
            className="notification icon-button"
            onClick={() => navigate("/notifications")}
          >
            <Icon d="M5 17h14l-2-4V9a5 5 0 0 0-10 0v4z M10 21h4" />
            {/* the design's red dot (no number); the count is in the title */}
            {badge > 0 && <i aria-hidden="true" />}
          </button>
          <button className="language" type="button" onClick={() => setDialog("language")}>
            <Icon d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0 M3 12h18 M12 3c-5 5-5 13 0 18 M12 3c5 5 5 13 0 18 M5 7h14 M5 17h14" />
            <span>RU</span>
            <Icon d="m7 10 5 5 5-5" />
          </button>
          <button className="top-account" onClick={() => setDialog("account")}>
            <Avatar photo={photo} />
            <span>
              <strong>{name}</strong>
              <small>{roleLabel}</small>
            </span>
            <Icon d="m7 10 5 5 5-5" />
          </button>
        </div>
      </header>
      {dialog === "account" && (
        <Dialog title={name} onClose={() => setDialog(null)}>
          <p>{person ? `${person} · ` : ""}{roleLabel}</p>
          <div className="actions">
            <button className="button" type="button" onClick={() => { setDialog(null); navigate("/settings"); }}>Профиль и настройки</button>
            <button className="button primary" type="button" onClick={logout}>Выйти</button>
          </div>
        </Dialog>
      )}
      {dialog === "language" && (
        <Dialog title="Язык интерфейса" onClose={() => setDialog(null)}>
          <p>Текущий язык: Русский.</p>
          <div className="actions">
            <button className="button primary" type="button" onClick={() => setDialog(null)}>Понятно</button>
          </div>
        </Dialog>
      )}
    </>
  );
}
